<template>
  <div class="negativeTopbarMargin">

    <v-parallax v-if="isDoneLoading && !headerImg" height=200 :src="parallaxImg">
      <v-layout align-center column justify-center>
        <h1 class="display-3 font-weight-thin mb-3"><router-link class="white--text nodeco" to="/">Turing Form</router-link></h1>
        <h4 class="subheading text-xs-center">Simple and free form/survey for anything.</h4>
        <span>Made with <router-link to="/" class="white--text">TuringForm</router-link></span>
      </v-layout>
    </v-parallax>

    <v-img v-if="headerImg" height=150 contain :src="headerImg" class="mt-5">
    </v-img>

    <v-container style="max-width: 1000px">

      <div v-if="isDoneLoading && isCopiable">
        <v-layout wrap class="mt-5 align-center justify-end row fill-height">
          <span v-if="!isAuthenticated">Please <router-link to="/login">login</router-link> to copy this form</span>
        </v-layout>

        <v-layout wrap class="align-center justify-end row fill-height">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn color="accent" v-on="on" @click="copyForm" :disabled="!isAuthenticated">
                <v-icon>file_copy</v-icon><span class="ml-1">Copy Form</span>
              </v-btn>
            </template>
            <span v-if="isAuthenticated">Results are not copied</span>
          </v-tooltip>
        </v-layout>
      </div>

      <v-layout wrap class="row">

        <v-flex>
          <v-card class="elevation-0">
            <v-card-text>
              <p v-if="form.length > 0 && owners.length === 0" class="text-center">
                This is a public form. You can view the result <a :href="'/report/' + formId">here</a>
              </p>
              <h1>{{formTitle}} </h1>
            </v-card-text>
          </v-card>
        </v-flex>

      </v-layout>

      <v-stepper v-model="currentQuestionIndex" vertical v-if="form.length > 0" class="elevation-0">

        <div v-for="(questionObj, index) in form">
          <v-stepper-step :complete="currentQuestionIndex > index" :step="index+1" :editable="editable" :rules="[()=>checkAnswer(index+1)]">
            <span class="headline" v-html="renderQuestionTitle(questionObj.text)"></span>
            <span class="subheading" v-if="questionObj.answer">Your answer: {{renderAnswer(questionObj.answer)}}</span>
          </v-stepper-step>

          <v-stepper-content :step="index+1" class="pl-0">
            
            <Checkbox  v-if="questionObj.type === formTypes.checkbox" :question=questionObj></Checkbox>
            <Textbox   v-else-if="questionObj.type === formTypes.textbox" :question=questionObj></Textbox>
            <SliderBox v-else-if="questionObj.type === formTypes.sliderbox" :question=questionObj></SliderBox>
            <DateBox   v-else-if="questionObj.type === formTypes.datebox" :question=questionObj></DateBox>
            <RatingBox v-else-if="questionObj.type === formTypes.ratingbox" :question=questionObj></RatingBox>
            <TimeBox   v-else-if="questionObj.type === formTypes.timebox" :question=questionObj></TimeBox>
            <RadioBox  v-else-if="questionObj.type === formTypes.radiobox" :question=questionObj></RadioBox>
            <PhotoBox  v-else-if="questionObj.type === formTypes.photobox" :question=questionObj></PhotoBox>
            <NumberBox v-else-if="questionObj.type === formTypes.numberbox" :question=questionObj></NumberBox>

            <v-btn block color="primary" @click="currentQuestionIndex++"> {{form.length <= (index+1) ? "Done" : "Next"}} </v-btn>
            
          </v-stepper-content>
        </div>

      </v-stepper>

      <transition name="fade">
        <h1 class="text-xs-center" v-if="currentQuestionIndex > 1 && currentQuestionIndex > form.length 
        && !saveError" >All Done. Thank you.</h1>
      </transition>

      <v-alert :value="saveError" color="error" icon="warning" outline>
        Something went wrong. Our team is notified. Please try again later.
      </v-alert>

      <ShareButtons class="mb-5 mt-5"></ShareButtons>

    </v-container>

  </div>
</template>

<script>
  import Checkbox from '@/components/formView/Checkbox.vue'
  import Textbox from '@/components/formView/Textbox.vue'
  import SliderBox from '@/components/formView/SliderBox.vue'
  import DateBox from '@/components/formView/DateBox.vue'
  import RatingBox from '@/components/formView/RatingBox.vue'
  import TimeBox from '@/components/formView/TimeBox.vue'
  import RadioBox from '@/components/formView/RadioBox.vue'
  import PhotoBox from '@/components/formView/PhotoBox.vue'
  import NumberBox from '@/components/formView/NumberBox.vue'
  import ShareButtons from '@/components/ShareButtons.vue'
  import ParallaxImg from '@/assets/parallax.jpeg'

  export default {
    components: {
      Checkbox,
      Textbox,
      SliderBox,
      DateBox,
      RatingBox,
      TimeBox,
      RadioBox,
      PhotoBox,
      NumberBox,
      ShareButtons,
      ParallaxImg
    },
    data: () => ({
      parallaxImg : ParallaxImg.toString(),
      showSyncDialog : false,
      formTitle    : null,
      currentQuestionIndex : 1,
      saveError    : false,
      db           : firebase.firestore(),
      editable     : true,
      keyListenr   : null,
      isDoneLoading: false,
      headerImg    : "",
      owners       : [],
      isFormOwnerPremium : false,
      isCopiable : true,
      viewCount : 0
    }),
    computed: {
      form() {
        return this.$store.state.form;
      },
      formId(){
        return this.$route.params.formId;
      },
      answerId() {
        return "ans_" + this.$uuid.v1().replace(/-/g, "");
      },
      currentUser() {
        return this.$store.state.user;
      },
      isAuthenticated() {        
        return this.$store.state.user.authenticated;
      },
      isPremium() {
        return this.$store.state.isPremium;
      }
    },
    watch: {

      currentQuestionIndex() {
        if (this.currentQuestionIndex > this.form.length) {
          // All answering is done. Save
          this.editable = false;
          
          const _answerId = this.answerId;
          this.db
          .collection("answers")
          .doc(this.formId)
          .collection('answers')
          .doc(_answerId).set({
            answerId : _answerId,
            title: this.formTitle,
            data: this.form,
            createdAt: (new Date()).toISOString(),
            updatedAt: (new Date()).toISOString()
          })
          .then((docRef)=> {
            // console.log("Document written with ID: ", docRef.id);
            this.saveError = false;
          })
          .catch((error)=> {
            console.error("Error adding document: ", error);
            this.saveError = true;
          });

        }
      }
    },
    created() {

      // Remove Vuex cache
      this.$store.commit('setForm', []);

      // Load form data
      this.db.collection("forms").doc(this.formId).get().then((doc)=> {
        
        this.copyableForm = doc.data();
        this.$store.commit('setForm', doc.data().data);
        this.formTitle = doc.data().title;

        //* Misc form configs BEGIN *//

        // The header image must be under 200 px in height.
        this.headerImg = doc.data().headerImage;

        if (doc.data().copiable !== undefined){
          this.isCopiable = doc.data().copiable;  
        }
        //* Misc form configs END *//

        // No support for multiple owners yet
        this.owners = doc.data().owners || [];

        if (!this.owners || this.owners.length === 0) {
          this.isDoneLoading = true;
          return;
        }

        // Check if the form is premium form
        this.db.collection("users").doc(this.owners[0]).get().then((doc)=> {

          if (doc.data()) {
            this.isFormOwnerPremium = doc.data().premium;
            this.$store.commit('setPremiumFormState', doc.data().premium);  
          }
          this.isDoneLoading = true;
        });
        
      }).catch((error)=> {
        console.log("Error getting document:", error);
      });


      this.countView();
      this.getViews();
      
      
    },
    mounted() {

      this.keyListenr = window.addEventListener("keypress", e => {
        if (e.key === 'Enter') {
          this.currentQuestionIndex++;
        }
      });
    },

    destroy() {
      // Make sure the key listener is cleared before destroying the form.
      // Otherwise, this leads to memory leak
      this.keyListenr = null;
    },

    methods: {
      renderAnswer(answer) {
        if (Array.isArray(answer)) {
          return answer.join(", ");
        }
        return answer;
      },

      checkAnswer(index) {
        if (this.currentQuestionIndex > index 
            && !this.form[index-1]['answer']) {
          // Check if answer exists
          return false;
        }
        return true;
      },

      renderQuestionTitle(text){
        if (text){
          return text.replace(/\n/g, "<br>");  
        }
        return text;
      },

      copyForm(){
        var clonedForm = _.cloneDeep(this.form);
        const newFormId = "f_" + this.$uuid.v1().replace(/-/g, "");

        _.each(clonedForm, (question)=>{
          const newQid = this.$uuid.v4();
          question.qid = newQid;
        });
        

        // TODO: Redirect to login with a proper dialog
        if (!this.isAuthenticated) {
          return this.$router.push({ path: '/login' });
        } 

        this.db.collection("forms").doc(newFormId).set({
          title: this.formTitle,
          data: clonedForm,
          createdAt: (new Date()).toISOString(),
          updatedAt: (new Date()).toISOString(),
          owners: [this.currentUser.uid]
        })
        .then((docRef)=> {
          console.log("Document written with ID: ", docRef);
          return this.$router.push({ path: '/myforms' });
        })
        .catch((error)=> {
          console.error("Error adding document: ", error);
          // if (cb) cb(error);
        });
        
        
      },

      countView(){
        // View count for the current form
        const viewCountKey = 'viewCounted-' + this.formId;
        if (!window.localStorage.getItem(viewCountKey)){
          this.db
          .collection("views")
          .doc(this.formId)
          .collection('counts')
          .doc((new Date()).toISOString()).set({
            referrer: document.referrer
          })
          .then(()=>{
            window.localStorage.setItem(viewCountKey, true);
          })
          .catch((error)=> {
            console.log("Error setting view count:", error);
          });
        }
      },
      getViews(){
        this.db
          .collection("views")
          .doc(this.formId)
          .collection('counts')
          .get().then((snapshots)=> {
            this.viewCount = snapshots.size;
        });
      }
    }
  }
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
